import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Eye, 
  Trash2, 
  Download,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  MoreHorizontal,
  RefreshCw,
  FileText,
  Settings,
  CheckCircle,
  AlertTriangle,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';

// Timesheet Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { color: 'warning', text: 'Draft', bgColor: '#fef3c7', textColor: '#92400e' },
    submitted: { color: 'info', text: 'Submitted', bgColor: '#dbeafe', textColor: '#1e40af' },
    approved: { color: 'success', text: 'Approved', bgColor: '#d1fae5', textColor: '#065f46' },
    rejected: { color: 'danger', text: 'Rejected', bgColor: '#fee2e2', textColor: '#991b1b' }
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  
  return (
    <span 
      className="badge" 
      style={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        fontFamily: 'var(--font-family)'
      }}
    >
      {config.text}
    </span>
  );
};

// Action Dropdown Component
const ActionDropdown = ({
  timesheet,
  onView,
  onEdit,
  onDelete,
  onApprove,
  onReject,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 180);
    }
    setIsOpen((v) => !v);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 10000 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[190px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onView(timesheet);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Timesheet
              </button>
            </li>

            <li>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onEdit(timesheet);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit Timesheet
              </button>
            </li>

            {timesheet.status === 'submitted' && (
              <>
                <li>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onApprove(timesheet);
                    }}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 text-left"
                  >
                    <CheckCircle size={16} className="mr-2.5 text-emerald-600" />
                    Approve
                  </button>
                </li>

                <li>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onReject(timesheet);
                    }}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
                  >
                    <AlertTriangle size={16} className="mr-2.5 text-red-600" />
                    Reject
                  </button>
                </li>
              </>
            )}

            <li className="border-t border-gray-200">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onDelete(timesheet);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete Timesheet
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  trend = 'up', // 'up' | 'down'
  trendValue = null,
  iconBg,
  iconColor,
  suffix = '',
}) => {
  const isUp = trend === 'up';

  return (
    <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
      {/* Accent line */}
      <div className="h-1 w-full bg-[var(--primary-color)]" />

      {/* Content */}
      <div className="relative flex items-center gap-3 p-4">
        {/* Trend */}
        {trendValue !== null && (
          <div
            className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
            style={{ color: isUp ? '#059669' : '#DC2626' }}
          >
            {isUp ? (
              <ArrowUpRight size={14} />
            ) : (
              <ArrowDownRight size={14} />
            )}
            +{trendValue}%
          </div>
        )}

        {/* Icon */}
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center"
          style={{ backgroundColor: iconBg }}
        >
          <Icon size={20} style={{ color: iconColor }} />
        </div>

        {/* Text */}
        <div>
          <div className="text-[20px] font-bold text-[var(--text-primary)]">
            {value}
            {suffix}
          </div>
          <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
            {title}
          </div>
        </div>
      </div>
    </div>
  );
};

const Timesheets = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // State management
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalTimesheets: 0,
    pendingApproval: 0,
    approvedThisWeek: 0,
    totalHours: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  
  const loadTimesheets = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString()
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.TIMESHEETS}?${queryParams}`, {
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to load timesheets');
      }

      const data = await response.json();
      const timesheetData = data.data || [];

      const mappedTimesheets = timesheetData.map((timesheet) => {
        const projects = (timesheet.timeEntries || []).flatMap((day) =>
          (day.entries || [])
            .map((entry) => entry.projectName || entry.projectLabel)
            .filter(Boolean)
        );

        return {
          id: timesheet._id || timesheet.id,
          week: timesheet.weekStarting,
          weekEnding: timesheet.weekEnding,
          employee: timesheet.employeeName || '',
          department: timesheet.department || '',
          totalHours: timesheet.totalHours || 0,
          billableHours: timesheet.billableHours || 0,
          nonBillableHours: timesheet.nonBillableHours || 0,
          status: timesheet.status || 'draft',
          submittedAt: timesheet.submittedAt,
          approvedAt: timesheet.approvedAt,
          approvedBy: timesheet.approvedBy,
          entries: (timesheet.timeEntries || []).reduce((sum, day) => sum + (day.entries || []).length, 0),
          projects: [...new Set(projects)]
        };
      });

      setTimesheets(mappedTimesheets);
      setStats({
        totalTimesheets: data.pagination?.totalItems || mappedTimesheets.length,
        pendingApproval: mappedTimesheets.filter(t => t.status === 'submitted').length,
        approvedThisWeek: mappedTimesheets.filter(t => t.status === 'approved').length,
        totalHours: mappedTimesheets.reduce((sum, t) => sum + t.totalHours, 0)
      });

      setPagination(prev => ({
        ...prev,
        totalItems: data.pagination?.totalItems || mappedTimesheets.length,
        totalPages: data.pagination?.totalPages || 1,
        currentPage: data.pagination?.currentPage || prev.currentPage
      }));
    } catch (error) {
      console.error('Error loading timesheets:', error);
      showError(error.message || 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  // Load timesheets on component mount and pagination change
  useEffect(() => {
    loadTimesheets();
  }, [pagination.currentPage, pagination.itemsPerPage]);

  const handleRefresh = () => {
    loadTimesheets();
  };

  const handleCreateTimesheet = () => {
    navigate('/projects/time-tracking/timesheets/create');
  };

  const handleViewTimesheet = (timesheet) => {
    navigate(`/projects/time-tracking/timesheets/view/${timesheet.id}`);
  };

  const handleEditTimesheet = (timesheet) => {
    navigate(`/projects/time-tracking/timesheets/edit/${timesheet.id}`);
  };

  const handleDeleteTimesheet = (timesheet) => {
    setTimesheetToDelete(timesheet);
    setShowDeleteModal(true);
  };

  const handleApproveTimesheet = async (timesheet) => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.TIMESHEETS}/${timesheet.id}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify({ status: 'approved' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to approve timesheet');
      }

      success('Timesheet approved successfully');
      loadTimesheets();
    } catch (error) {
      console.error('Error approving timesheet:', error);
      showError(error.message || 'Failed to approve timesheet');
    }
  };

  const handleRejectTimesheet = async (timesheet) => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.TIMESHEETS}/${timesheet.id}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify({ status: 'rejected' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to reject timesheet');
      }

      success('Timesheet rejected');
      loadTimesheets();
    } catch (error) {
      console.error('Error rejecting timesheet:', error);
      showError(error.message || 'Failed to reject timesheet');
    }
  };

  const confirmDelete = async () => {
    if (!timesheetToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.TIMESHEETS}/${timesheetToDelete.id}`, {
        method: 'DELETE',
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete timesheet');
      }

      success('Timesheet deleted successfully');
      loadTimesheets();
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      showError(error.message || 'Failed to delete timesheet');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setTimesheetToDelete(null);
    }
  };
  
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh timesheets',
      disabled: loading,
    },
    {
      label: 'New Timesheet',
      icon: <Plus size={18} />,
      onClick: handleCreateTimesheet,
      variant: 'primary',
      tooltip: 'Create a new timesheet',
    },
  ];

  const filteredTimesheets = timesheets.filter((t) => {
  const matchesSearch =
    t.employee.toLowerCase().includes(search.toLowerCase()) ||
    t.projects.join(', ').toLowerCase().includes(search.toLowerCase());

  const matchesStatus =
    statusFilter === 'all' || t.status === statusFilter;

  return matchesSearch && matchesStatus;
});
  // Table columns
  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'week',
      label: 'Week',
      render: (value, timesheet) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            Week of {new Date(timesheet.week).toLocaleDateString()}
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            Ending {new Date(timesheet.weekEnding).toLocaleDateString()}
          </div>
        </div>
      )
    },
    {
      key: 'employee',
      label: 'Employee',
      render: (value, timesheet) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            {timesheet.employee}
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {timesheet.department}
          </div>
        </div>
      )
    },
    {
      key: 'totalHours',
      label: 'Total Hours',
      render: (value, timesheet) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            {timesheet.totalHours}h
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {timesheet.billableHours}h billable, {timesheet.nonBillableHours}h non-billable
          </div>
        </div>
      )
    },
    {
      key: 'projects',
      label: 'Projects',
      render: (value, timesheet) => (
        <div>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {timesheet.projects.slice(0, 2).join(', ')}
            {timesheet.projects.length > 2 && ` +${timesheet.projects.length - 2} more`}
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {timesheet.entries} entries
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, timesheet) => <StatusBadge status={timesheet.status} />
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      render: (value, timesheet) => (
        <div>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {timesheet.submittedAt ? new Date(timesheet.submittedAt).toLocaleDateString() : 'Not submitted'}
          </div>
          {timesheet.approvedBy && (
            <div style={{ color: '#6b7280', fontSize: '12px' }}>
              Approved by {timesheet.approvedBy}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, timesheet) => (
        <ActionDropdown 
          timesheet={timesheet}
          onView={handleViewTimesheet}
          onEdit={handleEditTimesheet}
          onDelete={handleDeleteTimesheet}
          onApprove={handleApproveTimesheet}
          onReject={handleRejectTimesheet}
        />
      )
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Timesheets"
        description="Manage and approve weekly timesheets for your team"
        actions={headerActions}
      />
      <div className="page-container">

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        <StatCard
        title="Total Timesheets"
        value={stats.totalTimesheets}
        icon={FileText}
        iconBg="#E0F2FE"
        iconColor="#0284C7"
        trend="up"
        trendValue={8}
       />

        <StatCard
        title="Pending Approval"
        value={stats.pendingApproval}
        icon={AlertTriangle}
        iconBg="#FEF3C7"
        iconColor="#D97706"
        trend="down"
        trendValue={4}
        />

       <StatCard
       title="Approved This Week"
       value={stats.approvedThisWeek}
       icon={CheckCircle}
       iconBg="#DCFCE7"
       iconColor="#16A34A"
       trend="up"
       trendValue={12}
       />

       <StatCard
       title="Total Hours"
       value={stats.totalHours.toFixed(1)}
       suffix="h"
       icon={Clock}
       iconBg="#EDE9FE"
       iconColor="#7C3AED"
       trend="up"
       trendValue={6}
      />
      </div>
      
      <DataTable
      data={filteredTimesheets}
      columns={columns}
      title="Timesheets"
      loading={loading}
      pagination={pagination}
      onPaginationChange={setPagination}
      emptyMessage="No timesheets found. Create your first timesheet to get started."
      emptyIcon={<FileText size={48} />}
      showActions={false}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
      isOpen={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onConfirm={confirmDelete}
      title="Delete Timesheet"
      message={`Are you sure you want to delete the timesheet for ${
      timesheetToDelete
      ? new Date(timesheetToDelete.week).toLocaleDateString()
      : ''
      }? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
      loading={isDeleting}
     />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default Timesheets;
